<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    hasClose?: boolean
    isOpen?: boolean
    shouldCloseOnEsc?: boolean
    title: string
  }>(),
  {
    hasClose: true,
    shouldCloseOnEsc: true,
    isOpen: false,
  },
)

const emit = defineEmits<{
  close: []
}>()

const modalRef = ref<HTMLDivElement>()

const previouslyFocusedElement = ref<Element | HTMLElement | null>()

const focusableElements = computed(() => {
  const modalElement = modalRef.value

  const focusElements =
    modalElement?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, [role="button"], textarea, area, [tabindex]:not([tabindex="-1"])',
    ) || []

  const [first] = focusElements

  return { first, last: focusElements[focusElements.length - 1] || first }
})

const closeModal = (): void => {
  emit('close')
}

const triggerInitialFocus = (): void => {
  nextTick(() => {
    const zeroTabIndexElement =
      modalRef.value?.querySelector<HTMLElement>('[tabindex="0"]')
    const elementToFocus = zeroTabIndexElement || focusableElements.value.first
    elementToFocus?.focus?.()
  })
}

const restoreFocusedElement = (): void => {
  nextTick(() => {
    const el = previouslyFocusedElement.value
    if (el && 'focus' in el) {
      el.focus()
    }
  })
}

watch(
  () => props.isOpen,
  (newValue) => {
    useDisableBodyScroll(newValue)
    if (!newValue) {
      closeModal()
      restoreFocusedElement()
    } else {
      previouslyFocusedElement.value = document.activeElement
      triggerInitialFocus()
    }
  },
  { immediate: true },
)

const keyDownHandler = (e: KeyboardEvent): void => {
  const { key, shiftKey } = e

  if (key === 'Escape') {
    props.shouldCloseOnEsc && closeModal()
    return
  }

  /**
   * Focus trap (we do not let focus to go outside the modal)
   */
  const { first, last } = focusableElements.value
  if (key !== 'Tab') {
    return
  }
  if (shiftKey && document.activeElement === first) {
    e.preventDefault()
    last && last.focus()
    return
  }
  if (!shiftKey && document.activeElement === last) {
    e.preventDefault()
    first && first.focus()
  }
}

onMounted(() => {
  window.addEventListener('keydown', keyDownHandler)
})
onUnmounted(() => {
  window.removeEventListener('keydown', keyDownHandler)
})
</script>

<template>
  <div
    :aria-hidden="!isOpen"
    :class="['app-modal', { '--active': isOpen }]"
  >
    <div
      ref="modalRef"
      role="dialog"
      :aria-labelledby="title"
      class="app-modal__dialog"
      aria-modal="true"
    >
      <header
        :id="title"
        class="app-modal__header"
      >
        <slot name="title" />
        <UiButton
          v-if="hasClose"
          type="icon"
          @click="closeModal"
        >
          <Icon name="fa6-solid:x" />
          <span class="sr-only"> Close </span>
        </UiButton>
      </header>
      <slot />
      <slot name="footer" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@/assets/styles/helpers' as *;

.app-modal {
  @extend %stretch-fixed;

  visibility: hidden;
  opacity: 0;
  transition: background-color 200ms ease-in-out;

  &.--active {
    opacity: 1;
    z-index: 10;
    visibility: visible;
    display: flex;
    justify-content: center;
    align-items: center;
    background: var(--app-black-alpha-80);
  }
}

.app-modal__dialog {
  border: 0;
  box-shadow: var(--app-panel-shadow);
  max-width: 724px;
  border-radius: var(--app-border-radius-large);
  padding: var(--app-panel-spacing);
  background-color: var(--app-color-sendcloud-white);

  @include min-width('sm') {
    padding: var(--app-panel-spacing-desktop);
  }

  @include max-width('md') {
    margin: auto var(--app-spacer-16);
  }
}

.app-modal__header {
  display: flex;
  gap: var(--app-spacer-16);
  justify-content: space-between;
}
</style>
